'use client';
import Button from '@/components/atoms/button';
import Typography from '@/components/atoms/typography';
import Search from '@/components/organisms/search';
import Spinner from '@/components/organisms/spinner';
import React, { useEffect, useState } from 'react';
import DetailsTable from './components/order-details-table';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import { statusColor } from './helper';
import useIsCurrentPage from '@/lib/hooks/useIsCurrentPage';
import Modal from '@/components/organisms/modal';
import Accordion from '@/components/molecules/accordion';
import Markdown from '@/components/atoms/markdown';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { sdk } from '@/sdk/CommercetoolsSDK';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useAccount from '@/lib/hooks/useAccount';
import * as XLSX from 'xlsx';
import { dateFormat } from '../where-is-my-order/helpers';
import CategoriesBreadcrumb from '@/components/organisms/product-list/components/breadcrumb';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

export interface FAQSectionProps {
  data?: any;
}

export interface SectionType {
  question: string | undefined;
  answer: any;
}

const OrderTracking = ({ data }: FAQSectionProps) => {
  const Faqs = data?.sections[0]?.global_faq;
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [productType, setProductType] = useState<boolean>(false);
  const [purchaseDetails, setPurchaseDetails] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [lockSearch, setLockSearch] = useState(false);
  const { locale } = useParams();
  const orderParams = useSearchParams();
  const router = useRouter();
  const { translate } = useTranslation();
  const auPage = useIsCurrentPage(['/au']);
  const extensions = sdk.composableCommerce;
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  const [hasFetched, setHasFetched] = useState(false);
  const { loggedIn, account } = useAccount();

  const categoriesBreadcrumb = [{ name: 'Back to Homepage', link: '/' }];

  const handleTrack = async (searchText: any, retryCount = 0) => {
    const trimmedSearch = searchText?.trim();

    if (!trimmedSearch) {
      return;
    }

    // Validate before proceeding - CTBR only for NZ locale
    const isCTBRForNZ = !auPage && /^CTBR/i.test(trimmedSearch);
    const isValidForLocale = auPage ? /^AU/i.test(trimmedSearch) : /^NZ/i.test(trimmedSearch);

    if (!isCTBRForNZ && !isValidForLocale) {
      setErrorMessage('Please enter valid GWA order number');
      return; // Don't run if validation fails
    }

    let isMounted = true;

    try {
      setLoading(true);
      const requestData: any = {
        data: trimmedSearch,
      };
      if (loggedIn) {
        requestData.accNum = account?.email;
      }

      const responseBody = await extensions.custom.orderTrack(JSON.stringify(requestData));
      const regex = /Calling extension(?: \S+)? failed/;

      if (regex.test(responseBody?.error?.message) && retryCount < 3) {
        await handleTrack(searchText, retryCount + 1);
        return;
      }

      if (responseBody?.data?.status === 200) {
        setProductType(responseBody?.data.data.isBrochureOrder);

        const orderNumber = responseBody?.data?.data?.salesOrders?.some(
          (order: { orderNumber: any }) => order.orderNumber,
        );

        if (isMounted) {
          if (responseBody?.data?.data?.responseType === 'PurchaseOrder' && orderNumber) {
            setPurchaseDetails(responseBody?.data?.data);
            setOrderDetails(null);
            setErrorMessage(undefined);
          } else if (responseBody?.data?.data?.responseType === 'Order') {
            setOrderDetails(responseBody?.data?.data);
            setPurchaseDetails(null);
            setErrorMessage(undefined);
          } else {
            setOrderDetails(null);
            setPurchaseDetails(null);
            setErrorMessage('No orders found');
          }
        }
      } else {
        if (isMounted) {
          setErrorMessage('Please enter valid GWA order number');
          setOrderDetails(null);
          setPurchaseDetails(null);
        }
      }
    } catch (error) {
      console.error('Order tracking fetch error', error);
      if (isMounted && loggedIn) {
        setErrorMessage('Please enter valid GWA order number');
        setOrderDetails(null);
        setPurchaseDetails(null);
      }
    } finally {
      if (isMounted) setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      setErrorMessage('Please enter valid GWA order number');
      setOrderDetails(null);
      setPurchaseDetails(null);
    } else {
      // CTBR only works for NZ locale, AU/NZ work for their respective locales
      const isCTBRForNZ = !auPage && /^CTBR/i.test(trimmedValue);
      const isValidForLocale = auPage ? /^AU/i.test(trimmedValue) : /^NZ/i.test(trimmedValue);

      if (!isCTBRForNZ && !isValidForLocale) {
        setErrorMessage('Please enter valid GWA order number');
        setOrderDetails(null);
        setPurchaseDetails(null);
      } else {
        setErrorMessage(undefined);
      }
    }
  };

  const handleKeyDown = (e: any) => {
    setErrorMessage(undefined);
    e.key === 'Enter' && handleTrack(searchText);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleRefresh = () => {
    router.push(`/${locale}/order-tracking`);
    setSearchText('');
    setOrderDetails(null);
    setErrorMessage(undefined);
    setPurchaseDetails(null);
    setOrder(null);
    setLockSearch(false);
  };

  useEffect(() => {
    const order = orderParams.get('order');
    const lock = orderParams.get('unique');
    const ct = orderParams.get('ct');

    if (loggedIn && order && lock && !hasFetched) {
      setLockSearch(true);
    }

    if (order && !hasFetched) {
      let displayedOrder = order;
      if (ct === 'true') {
        const lastHyphenIndex = order.lastIndexOf('-');
        if (lastHyphenIndex !== -1) {
          displayedOrder = `${order.substring(0, lastHyphenIndex + 1)}xxxx`;
        }
      }
      setSearchText(displayedOrder);
      setOrder(order);
      setPurchaseDetails(null);

      if (loggedIn) {
        handleTrack(order);
        setHasFetched(true);
      } else if (!loggedIn) {
        handleTrack(order);
        setHasFetched(true);
      }

      if (!lock) {
        setLockSearch(false);
      }
    }
  }, [orderParams, router, loggedIn]);

  useEffect(() => {
    if (productType && orderDetails?.ctOrderNumber) {
      setSearchText(orderDetails.ctOrderNumber);
    }
  }, [productType, orderDetails]);

  useEffect(() => {
    isModalOpen && document.body.classList.add('root_overflow_hidden');
    !isModalOpen && document.body.classList.remove('root_overflow_hidden');
  }, [isModalOpen]);

  const handleNavigateClaim = (orderNumber: string) => {
    router.push(`/claim-form?order_number=${orderNumber}`);
  };

  const checkETALogic = (estimatedDeliveryDate: Date | null) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 60);

    const invalidDate = new Date(1900, 0, 1, 12);

    if (!estimatedDeliveryDate) {
      return { isETAWrong: true, displayDate: '-' };
    }

    const cleanEstimatedDeliveryDate = new Date(estimatedDeliveryDate);
    cleanEstimatedDeliveryDate.setHours(0, 0, 0, 0);

    if (
      cleanEstimatedDeliveryDate > maxDate ||
      cleanEstimatedDeliveryDate.getTime() === invalidDate.getTime() ||
      cleanEstimatedDeliveryDate < today
    ) {
      return { isETAWrong: true, displayDate: '-' };
    }

    const formattedDate = dateFormat(cleanEstimatedDeliveryDate.toISOString(), 'dd/MM/yyyy');
    return { isETAWrong: false, displayDate: formattedDate };
  };

  const handleDownloadExcel = () => {
    if (!orderDetails?.salesOrderLines?.length) return;

    const data = orderDetails.salesOrderLines.map((line: any) => {
      const { displayDate } = checkETALogic(line.estimatedDeliveryDate ?? null);

      return {
        No: line.serialNumber === 0 ? '-' : line.serialNumber,
        'Item Number': line.itemNumber,
        'Product Name': line.itemName,
        'Qty Ordered': line.isBundle ? '-' : line.qtyOrdered,
        'Qty Shipped': line.isBundle ? '-' : line.qtyShipped,
        'Est. Delivery Date': line.isBundle ? '-' : displayDate,
        'Delivered Date': line.isBundle
          ? '-'
          : line.status === 'Delivered' && line?.shipments?.[0]?.deliveryDate
            ? dateFormat(line.shipments[0].deliveryDate, 'dd/MM/yyyy')
            : '-',
        Status: line.status,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Lines');

    const orderNumber = orderDetails?.orderNumber || orderDetails?.ctOrderNumber || 'order';
    const poNumber = orderDetails?.poNumber ? `_${orderDetails.poNumber}` : '';

    XLSX.writeFile(workbook, `${orderNumber}${poNumber}_tracking.xlsx`);
  };
  return (
    <>
      {loading && <Spinner />}
      {loading && <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>}
      {!loggedIn && (
        <div className="mt-2 flex gap-1 px-5 lg:px-10 text-12 leading-tight hover:text-[#000000]">
          {' '}
          <ChevronLeftIcon className="h-4 w-4 text-gray-700 cursor-pointer" onClick={() => router.push('/')} />
          <CategoriesBreadcrumb breadcrumb={categoriesBreadcrumb} />{' '}
        </div>
      )}

      <div className={`md:pt-0 ${loggedIn ? 'mt-4 pt-[1.375rem] md:mt-10' : 'mt-10 px-5 md:mt-20 lg:px-10'}`}>
        <div className="mb-10 md:mb-5">
          <Typography className="!text-32 !font-medium !leading-9 text-[#000000]">Where's my order</Typography>
        </div>
        <div className="mb-10 flex flex-col items-center gap-x-5 gap-y-[1.125rem] sm:flex-row">
          <div className="relative w-full flex-1">
            <Search
              disabled={lockSearch}
              noSearchButton={true}
              scrollControl={false}
              variant="sm"
              filterSearch={false}
              placeholder={'Enter GWA Order number'}
              searchValue={searchText}
              handleOnChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              inputContinerClassName={`border ${errorMessage ? '!border-red-600' : '!border-[#DDDDDD]'
                } rounded-[6px] overflow-hidden py-[10.2px]`}
            />
            {errorMessage && (
              <Typography className="relative mt-2 text-red-600 sm:absolute sm:-bottom-6 sm:left-0 sm:mt-0">
                {errorMessage}
              </Typography>
            )}
          </div>
          <div className="w-full sm:w-fit">
            <Button
              disabled={lockSearch}
              size="custom"
              iconPosition="left"
              variant="secondary"
              className={`border-Neutral-600 w-full cursor-pointer justify-center whitespace-nowrap rounded-none border-2 !bg-gray-700 px-[1.563rem] py-[0.969rem] text-16 !font-medium !text-[#FFFFFF] disabled:!bg-gray-500 sm:w-fit`}
              onClick={() => handleTrack(searchText)}
            >
              Track
            </Button>
          </div>
        </div>
        {lockSearch && (
          <div className="-mt-6 mb-2 flex w-full justify-end gap-1">
            <Typography>Track another Order?</Typography>
            <Typography onClick={handleRefresh} className="cursor-pointer text-blue-500 underline">
              Click Here!
            </Typography>
          </div>
        )}
        <div className="">
          {orderDetails && (
            <div className="mb-8 sm:mb-10">
              <Typography className="mb-3 !text-[14px] !font-semibold uppercase !leading-[24px] text-[#A7A7A7]">
                Order Details
              </Typography>
              <div className="">
                <div className="mb-7 flex flex-col gap-x-5 gap-y-3 sm:mb-10 md:flex-row">
                  <Typography className="!text-32 !font-medium uppercase !leading-9 text-black">
                    {productType ? orderDetails.ctOrderNumber : orderDetails?.orderNumber || searchText}
                  </Typography>
                  {orderDetails?.status && (
                    <Button
                      size="custom"
                      variant="secondary"
                      className={`justify-center whitespace-nowrap !rounded-[3rem] px-[1.563rem] py-[0.125rem] !text-[14px] !font-medium !leading-[0.561rem] text-black ${statusColor(
                        orderDetails?.status,
                      )} !h-[1.875rem] w-fit border-none`}
                    >
                      {orderDetails?.status}
                    </Button>
                  )}
                </div>
                <div className="">
                  {loggedIn && (
                    <div className="flex flex-col gap-x-5 gap-y-3 border-b border-[#EEEEEE] py-4 md:flex-row">
                      <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909]">
                        Customer:
                      </Typography>
                      <div className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                        {account?.firstName} {account?.lastName}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col border-[#EEEEEE] md:flex-row md:border-b">
                    <div className="flex w-full gap-x-5 border-b border-[#EEEEEE] py-4 md:border-none">
                      <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909]">
                        Order number:
                      </Typography>
                      <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                        {productType
                          ? orderDetails?.ctOrderNumber
                          : orderDetails?.orderNumber || `${orderDetails?.orderNumber?.startsWith('AU') ? 'NZ01' : 'AU01'}-xxxx`}
                      </Typography>
                    </div>
                    {loggedIn && <div className="flex w-full gap-x-5 border-b border-[#EEEEEE] py-4 md:border-none">
                      <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909] whitespace-nowrap">
                        Invoice number:
                      </Typography>
                      <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                        {Array.from(new Set(
                          orderDetails?.salesOrderLines
                            ?.map((line: any) => line.invoiceNumbers)
                            .filter((inv: string) => inv)
                        )).join(', ') || 'N/A'}</Typography>
                    </div>}
                    {!loggedIn && (
                      <div className=" hidden md:flex w-full gap-x-5 gap-y-3 border-b border-[#EEEEEE] py-4">
                        <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909]">
                          Requested delivery date:
                        </Typography>
                        <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                          {/* {orderDetails?.requestedDeliveryDate ? dateFormat(orderDetails?.requestedDeliveryDate) : 'TBD'} */}
                          {orderDetails?.requestedDeliveryDate
                            ? dateFormat(orderDetails.requestedDeliveryDate, 'dd/MM/yyyy')
                            : productType
                              ? 'N/A'
                              : 'TBD'}
                        </Typography>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col border-[#EEEEEE] md:flex-row md:border-b">
                    <div className="flex flex-1 gap-x-5 border-b border-[#EEEEEE] py-4 md:border-none">
                      <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909]">
                        Order date:
                      </Typography>
                      <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                           {orderDetails?.orderDate  && orderDetails.orderDate.slice(0, 10).split('-').reverse().join('/')}
                      </Typography>
                    </div>
                    {loggedIn && (
                      <div className="flex flex-1 gap-x-5 border-b border-[#EEEEEE] py-4 md:border-none">
                        <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909]">
                          Requested delivery date:
                        </Typography>
                        <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                          {/* {orderDetails?.requestedDeliveryDate ? dateFormat(orderDetails?.requestedDeliveryDate) : 'TBD'} */}
                          {orderDetails?.requestedDeliveryDate
                            ? dateFormat(orderDetails.requestedDeliveryDate, 'dd/MM/yyyy')
                            : productType
                              ? 'N/A'
                              : 'TBD'}
                        </Typography>
                      </div>
                    )}
                    {!loggedIn && (
                      <div className="flex md:hidden flex-1 gap-x-5 border-b border-[#EEEEEE] py-4 md:border-none">
                        <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909]">
                          Requested delivery date:
                        </Typography>
                        <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                          {/* {orderDetails?.requestedDeliveryDate ? dateFormat(orderDetails?.requestedDeliveryDate) : 'TBD'} */}
                          {orderDetails?.requestedDeliveryDate
                            ? dateFormat(orderDetails.requestedDeliveryDate, 'dd/MM/yyyy')
                            : productType
                              ? 'N/A'
                              : 'TBD'}
                        </Typography>
                      </div>
                    )}

                  </div>
                  {loggedIn &&<div className="flex flex-col md:flex-row ">
                    <div className="flex flex-1 gap-x-5 py-4">
                      <Typography className="!text-[18px] !font-semibold !leading-[1.625rem] text-[#090909] min-w-[157px]">
                        Customer address: 
                      </Typography>
                      <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                        {orderDetails?.address ?? 'N/A'}
                      </Typography>
                    </div>
                  </div> }
                   
                </div>
              </div>
            </div>
          )}
          <div className="mb-10 bg-[#F4F4F4] p-3">
            <div className="flex gap-x-1 gap-y-2 sm:flex-row">
              <Typography className="!text-[16px] !font-normal !leading-6 text-[#090909]">
                Questions? Check out the
              </Typography>
              <Typography
                onClick={() => setIsModalOpen(true)}
                className="cursor-pointer !text-[16px] !font-bold !leading-6 text-[#090909] underline"
              >
                FAQ Section
              </Typography>
            </div>
          </div>
          {orderDetails?.salesOrderLines?.length > 0 ? (
            <div className={`${loggedIn ? '' : 'mb-16'}`}>
              <div className="mb-3 justify-between md:flex">
                <Typography className="!text-[24px] !font-medium !leading-[32px] text-black">
                  Order Lines Details
                </Typography>
                {/* <div className="mt-5 md:mt-0 flex gap-2 justify-end">
                  <Button
                    size="custom"
                    variant="secondary"
                    className={`border-Neutral-600 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap   rounded-none border-none !bg-[#A6C9CC] px-[0.815rem] py-[0.565rem]  py-[0.8rem] text-14 !font-medium !leading-[16px] !text-[#090909] disabled:!bg-gray-500 `}
                    onClick={handleDownloadExcel}
                  >
                    Download Order
                  </Button>
                </div> */}
              </div>
              <DetailsTable orderLines={orderDetails?.salesOrderLines} productType={productType} />
            </div>
          ) : (
            orderDetails && (
              <Typography className="!text-[18px] !font-medium !leading-[1.625rem] text-[#090909]">
                We are currently processing your order, please check again later...
              </Typography>
            )
          )}
        </div>
        <div className="">
          <Modal
            className="mx-auto flex !w-[95vw] max-w-[1216px] flex-col items-center justify-center sm:-mb-11 sm:overflow-auto md:mb-0 md:mt-0 xl:!w-full"
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            preventScroll={false}
            closeButton={true}
            variant="default"
            centered={true}
            size="fit"
          >
            <div
              className={`relative mx-auto !h-[90vh] overflow-y-auto !px-[18px] sm:h-[80vh] sm:max-h-[660px] sm:!px-[40px]`}
            >
              <div className="sticky top-0 mb-[1.125rem] border-b border-[#CCCCCC] bg-white !pt-5 pb-[1.125rem] sm:mb-11 sm:!pt-10 sm:pb-1">
                <Typography className="text-primary-black text-left text-[24px] !font-medium !leading-[38px] sm:text-center sm:!leading-[56px]">
                  {data?.title?.length > 0 ? data?.title : translate('product.faqs')}
                </Typography>
              </div>
              {Faqs?.accordion?.map((section: SectionType, index: number) => (
                <Accordion className={`border-b border-l-0 border-r-0 border-t-0 border-neutral-400`} key={index}>
                  <Accordion.Button className="!px-0 !py-4 text-left text-[22px] font-medium leading-[32px] text-[#333]">
                    {section?.question}
                  </Accordion.Button>
                  <Accordion.Panel>
                    <Markdown>{section?.answer}</Markdown>
                  </Accordion.Panel>
                </Accordion>
              ))}
              <Typography className="mt-5 !pb-5 !text-center !text-[18px] !font-medium !leading-[28px] !text-[#090909] sm:mt-[68px] sm:!pb-[62px]">
                {Faqs?.title?.length > 0
                  ? Faqs?.title
                  : "Can't find an answer? Please contact our Customer Service team on 13 14 16- Option 1"}
              </Typography>
            </div>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default OrderTracking;
